// src/pages/ConfirmGuestRescheduleShortstay.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";

const ConfirmGuestRescheduleShortstay: React.FC = () => {
  const { guestId, listingId, reservationId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get reservations and current listing from Redux
  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.guestReservations
  );
  const booking = reservations.find((r) => r.id === reservationId);

  const listing = useSelector((state: RootState) => state.shortstays.currentListing);

  useEffect(() => {
    if (listingId) {
      // subscribe to realtime updates
      const unsubscribe = dispatch(listenToShortstayData(listingId));
      return () => {
        if (typeof unsubscribe === "function") unsubscribe();
      };
    }
  }, [dispatch, listingId]);

  const handlePayment = async () => {
    if (!booking || !listing) return;
    setLoading(true);
    setError("");

    try {
      const payload = {
        userId: guestId,
        listingId,
        hostId: listing.hostId,
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        reservedDates: booking.reservedDates,
        totalCharged: booking.totalCharge,
        adults: booking.adults,
        children: booking.children,
        infants: booking.infants,
        pets: booking.pets,
        guestsFee: booking.guestsFee || 0,
        hostsFee: booking.hostsFee || 0,
        hostProfit: booking.hostProfit || 0,
        platformProfit: booking.platformProfit || 0,
      };

      const res = await axios.post(
        "https://areluxe.com/createCheckoutSession",
        payload
      );

      const data = res.data;
      const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;

      if (directUrl) {
        window.location.href = directUrl;
        return;
      }
      if (data?.id) {
        window.location.href = `https://checkout.stripe.com/c/pay/${data.id}`;
        return;
      }
      throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>Additional Payment Required</h2>

        {listing?.photos?.[0] && (
          <div style={styles.imageWrapper}>
            <img
              src={listing.photos[0]}
              alt="Listing Preview"
              style={styles.image}
            />
          </div>
        )}

        <div style={styles.info}>
          <strong>Listing:</strong> {listing?.title}
        </div>
        <div style={styles.info}>
          <strong>Dates:</strong> {booking?.checkIn} to {booking?.checkOut}
        </div>
        <div style={styles.info}>
          <strong>Nights:</strong> {booking?.reservedDates}
        </div>
        <div style={styles.info}>
          <strong>Total Due:</strong> ${booking?.totalCharge}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <button
          onClick={handlePayment}
          disabled={loading}
          style={styles.payButton}
        >
          {loading ? "Processing..." : `Pay $${booking?.totalCharge}`}
        </button>
      </div>
    </div>
  );
};

export default ConfirmGuestRescheduleShortstay;

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    backgroundColor: "#000",
    minHeight: "100vh",
    width: "100vw",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: "radial-gradient(ellipse at bottom, #333, #000)",
  },
  card: {
    background: "#111",
    borderRadius: 20,
    padding: "2rem",
    border: "1px solid",
    borderImage: "linear-gradient(to right, #aaa, silver) 1",
    width: "90%",
    maxWidth: 400,
    boxShadow: "0 0 20px rgba(255,255,255,0.1)",
    color: "#fff",
  },
  title: {
    textAlign: "center",
    marginBottom: "1.5rem",
    color: "#ddd",
  },
  info: {
    marginBottom: 10,
    fontSize: "1rem",
    color: "#bbb",
  },
  error: {
    color: "#f66",
    marginTop: 10,
    marginBottom: 10,
    fontWeight: "bold",
  },
  payButton: {
    background: "linear-gradient(135deg, #a67c00, silver)",
    color: "#111",
    border: "none",
    padding: "1rem",
    width: "100%",
    borderRadius: "40px",
    fontWeight: "bold",
    fontSize: "1.1rem",
    cursor: "pointer",
    marginTop: "1.5rem",
    transition: "all 0.3s ease",
  },
  imageWrapper: {
    width: "100%",
    height: 200,
    marginBottom: "1.5rem",
    overflow: "hidden",
    borderRadius: 16,
    border: "1px solid #333",
    boxShadow: "0 4px 20px rgba(255,255,255,0.05)",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "inherit",
  },
};
